/**
 * One person, 32px across: their photograph, or their two letters.
 *
 * Promoted out of `pages/client-roster/ui/RosterTable.tsx` on 3 September 2026, on the plan
 * library's client-plan rows — one diameter, one photograph branch, one monogram branch, and two
 * copies of it would drift the way this app's two search fields already did once.
 *
 * ── TWO canons draw it, and they disagree about four declarations ────────────────────────────
 * `KANON klienci v2 - D` writes a `--bg-sunken` ground with its letters at 10.5px / 700 / .03em;
 * `KANON Programy v2 - D3 (0.8)` writes `--bg-chip` at 9.5px / 800 / .02em, because the row around
 * it is four fifths of the size. Both were read off the rendered specimens. Nothing else predicts
 * which one a surface wants — the two discs are the same size, in the same kind of row, on the same
 * ground — so the variant names the CANON rather than pretending to a semantic that is not there.
 * The video analysis is a third canon, `clip`, whose surfaces set their own diameter.
 * An earlier revision of this file's header claimed the library's values and shipped the roster's;
 * that was wrong on the screen the moment wave 3 drew a client's plan.
 *
 * ── The two branches are SIBLINGS, never a layer under a layer ───────────────────────────────
 * The monogram is drawn INSTEAD of the photograph, not behind it. That is what lets `photoBox`'s
 * neutral ground be the placeholder AND the error state for the `<img>` without stacking two
 * placeholders: with a sized box and an empty `alt`, an image that 404s or is blocked leaves Chrome
 * drawing nothing at all, so the ground below it is what shows. See `shared/AGENTS.md`.
 *
 * ── Both branches are decorative, and that is a decision rather than an oversight ─────────────
 * `aria-hidden` on the letters, an empty `alt` on the photograph. Every surface that draws this
 * puts the person's NAME immediately beside it, and announcing it twice is noise rather than
 * information. A surface that ever draws an avatar with no name next to it has to say the name some
 * other way; it does not get to turn this one on.
 */

import photoBox from './photoBox.module.css';
import styles from './AvatarChip.module.css';

/**
 * Which canon's monogram this is. See the header: the two differ by ground, size, weight and tracking.
 * `clip` is the video analysis's person mark (no ring on either branch), and `clipSmall` its 16px
 * reading beside a name.
 */
export type AvatarChipVariant = 'roster' | 'library' | 'clip' | 'clipSmall';

interface AvatarChipProps {
  /** The two letters drawn when there is no photograph. */
  readonly initials: string;
  /** `null` when the person has no photograph, which is when the monogram is drawn. */
  readonly avatarUrl: string | null;
  /**
   * An invitation nobody has claimed: no fill, a dashed edge. It can only be true on the monogram
   * branch — an unclaimed address has no picture to belong to.
   */
  readonly pending?: boolean;
  /**
   * `lazy` for a thumbnail below the first screenful of a long list, which is the honest setting
   * there: the box is already reserved, so deferring the fetch moves nothing. Omitted above the
   * fold. The default is the browser's.
   */
  readonly loading?: 'lazy' | 'eager';
  /** Defaults to the roster's, which is the one that shipped first. Photographs are identical in both. */
  readonly variant?: AvatarChipVariant;
  /** The caller's place for it in a row. Layout only; see the stylesheet's header. */
  readonly className?: string;
}

export function AvatarChip({
  initials,
  avatarUrl,
  pending,
  loading,
  variant,
  className,
}: AvatarChipProps) {
  const clip = variant === 'clip' || variant === 'clipSmall';
  if (avatarUrl === null) {
    return (
      <span
        className={[
          styles.chip,
          variant === 'library' ? styles.library : '',
          clip ? styles.clip : '',
          variant === 'clipSmall' ? styles.clipSmall : '',
          pending === true ? styles.pending : '',
          className ?? '',
        ]
          .filter(Boolean)
          .join(' ')}
        aria-hidden
      >
        {initials}
      </span>
    );
  }

  return (
    <img
      className={[
        String(photoBox.photo),
        String(styles.photo),
        clip ? styles.clipPhoto : '',
        className ?? '',
      ]
        .filter(Boolean)
        .join(' ')}
      src={avatarUrl}
      alt=""
      loading={loading}
      decoding="async"
    />
  );
}
